import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  ChevronRight, 
  Brain, 
  TrendingUp, 
  Target,
  Wallet,
  PiggyBank,
  BarChart3,
  Sparkles,
  CheckCircle
} from 'lucide-react';

export default function Onboarding() {
  const [currentStep, setCurrentStep] = useState(0);
  const [isCompleting, setIsCompleting] = useState(false);

  const handleNext = () => {
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    } else {
      // Mark onboarding as complete and go to dashboard
      setIsCompleting(true);
      localStorage.setItem('onboarding_completed', 'true');
      // Force full page reload to dashboard to properly evaluate routing
      window.location.href = '/dashboard';
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    setIsCompleting(true);
    localStorage.setItem('onboarding_completed', 'true');
    // Force full page reload to dashboard to properly evaluate routing
    window.location.href = '/dashboard';
  };

  const steps = [
    {
      icon: <Brain className="w-20 h-20 text-teal-500" />,
      title: "Welcome to FinAI! 👋",
      subtitle: "Your AI-Powered Money Manager",
      description: "India's smartest financial planning app designed for salaried professionals like you.",
      features: [
        "Track all your finances in one place",
        "Get AI-powered recommendations",
        "Achieve your financial goals faster"
      ]
    },
    {
      icon: <BarChart3 className="w-20 h-20 text-purple-500" />,
      title: "Track Everything Effortlessly",
      subtitle: "Complete Financial Picture",
      description: "From daily expenses to long-term investments, manage it all seamlessly.",
      features: [
        "Income, expenses, and budgets",
        "Investments (stocks, MFs, PPF, EPF, FDs)",
        "Loans, bills, and net worth"
      ]
    },
    {
      icon: <Sparkles className="w-20 h-20 text-emerald-500" />,
      title: "Get Your AI Financial Plan",
      subtitle: "Personalized Just for You",
      description: "Answer a few questions and receive actionable recommendations in 5 minutes.",
      features: [
        "Emergency fund guidance",
        "Investment strategies",
        "Debt management tips"
      ]
    }
  ];

  const currentStepData = steps[currentStep];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Progress Indicator */}
        <div className="flex items-center justify-center mb-8 space-x-2">
          {steps.map((_, index) => (
            <div
              key={index}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentStep 
                  ? 'w-12 bg-gradient-to-r from-teal-500 to-emerald-500' 
                  : index < currentStep 
                    ? 'w-8 bg-teal-500' 
                    : 'w-8 bg-gray-700'
              }`}
            />
          ))}
        </div>

        {/* Main Content Card */}
        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-8 md:p-12 shadow-2xl">
          {/* Icon */}
          <div className="flex justify-center mb-6 animate-bounce-slow">
            {currentStepData.icon}
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold text-white text-center mb-2">
            {currentStepData.title}
          </h1>

          {/* Subtitle */}
          <p className="text-teal-400 text-center text-lg mb-4">
            {currentStepData.subtitle}
          </p>

          {/* Description */}
          <p className="text-gray-300 text-center text-lg mb-8">
            {currentStepData.description}
          </p>

          {/* Features List */}
          <div className="space-y-4 mb-8">
            {currentStepData.features.map((feature, index) => (
              <div 
                key={index} 
                className="flex items-start space-x-3 bg-gray-700/30 rounded-lg p-4 transform transition-all duration-300 hover:bg-gray-700/50"
                style={{ 
                  animationDelay: `${index * 100}ms`,
                  animation: 'slideInRight 0.5s ease-out forwards'
                }}
              >
                <CheckCircle className="w-6 h-6 text-teal-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-200 text-base">{feature}</span>
              </div>
            ))}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-700">
            {/* Left side: Skip or Previous */}
            {currentStep === 0 ? (
              <Button
                variant="ghost"
                onClick={handleSkip}
                disabled={isCompleting}
                className="text-gray-400 hover:text-white"
              >
                Skip Tour
              </Button>
            ) : (
              <Button
                variant="ghost"
                onClick={handlePrevious}
                disabled={isCompleting}
                className="text-gray-400 hover:text-white"
              >
                <ChevronRight className="mr-2 w-5 h-5 rotate-180" />
                Previous
              </Button>
            )}

            {/* Right side: Next or Get Started */}
            <Button
              onClick={handleNext}
              disabled={isCompleting}
              className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 px-8"
            >
              {isCompleting ? 'Loading...' : currentStep === 2 ? 'Get Started' : 'Next'}
              {!isCompleting && <ChevronRight className="ml-2 w-5 h-5" />}
            </Button>
          </div>

          {/* Step Counter */}
          <p className="text-center text-gray-500 text-sm mt-6">
            Step {currentStep + 1} of {steps.length}
          </p>
        </div>

        {/* Bottom Decoration Icons */}
        <div className="flex items-center justify-center space-x-8 mt-12 opacity-30">
          <Wallet className="w-8 h-8 text-teal-500" />
          <PiggyBank className="w-8 h-8 text-emerald-500" />
          <TrendingUp className="w-8 h-8 text-purple-500" />
          <Target className="w-8 h-8 text-orange-500" />
        </div>
      </div>

      {/* Custom animations */}
      <style jsx>{`
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
